<template>
  <v-container>
    <v-row>
      <v-col cols="9">
        <QCPhotoFilter :initial-filter="photoFilter" @apply-filters="setPhotoFilters($event)"></QCPhotoFilter>
      </v-col>
    </v-row>
    <v-progress-circular v-if="isLoading" indeterminate></v-progress-circular>

    <!-- Photo Cards with Count Panels for QC -->
    <v-row>
      <v-col
        v-for="photo in photos"
        :key="photo.id"
        :lg="6" :md="6" :sm="12" :xl="4" :xs="12"
      >
        <v-card class="mb-4">
          <v-card-title class="py-1">
            Response: {{ photo.response_id }}
            <v-chip v-if="photo.needs_review" color="warning" class="ml-2" small>
              Needs Review
            </v-chip>
            <v-chip v-if="photo.is_removed" color="error" class="ml-2" small>
              Removed
            </v-chip>
            <v-spacer />
            <span class="text-caption">Tagged by: {{ photo.tagger || 'Unknown' }}</span>
          </v-card-title>
          <image-zoom
              class="mb-3"
              :regular="photo.image_url"
              :zoom="photo.image_url"
              :zoom-amount="4"
              :click-zoom="true"
              img-class="count-image"
              :style="photo.is_removed ? 'opacity: 0.5' : ''"
            />
          <v-card-text>
            <CountPanel
              :ref="`countPanel_${photo.id}`"
              :photo="photo"
              :isQc="true"
              @saved="onCountSaved"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-container v-if="photos.length > 0" class="fixed border--primary">
      <v-btn
        class="mr-2 float-right"
        color="success"
        @click="submitBatch({getNext: true})"
      >
        Mark Reviewed & Get Next Batch
      </v-btn>
      <v-btn
        class="mr-2 float-right"
        color="primary"
        @click="submitBatch({getNext: false})"
      >
        Mark Reviewed & Close
      </v-btn>
    </v-container>
  </v-container>
</template>

<script>
import {SURFACES, buildPayload} from '@/components/filters/capabilities'
import { mapActions, mapMutations, mapState } from 'vuex'
import QCPhotoFilter from '@/components/filters/QCPhotoFilter'
import {routeFilterSeed} from '@/components/filters/routeSeed'
import CountPanel from '@/components/counts/CountPanel'
import imageZoom from 'vue-image-zoomer'
import axios from 'axios'

export default {
  name: 'CountQualityControl',
  components: {
    QCPhotoFilter,
    CountPanel,
    imageZoom
  },
  data() {
    return {
      photoFilter: {
        fromDt: null,
        thruDt: null,
        tags: [],
        projects: [],
        jobs: [],
        taggers: [],
        respIdSearchFreeForm: null,
        onlyNeedsReview: false,
        showQCed: false,
        includeInactive: false,
        country: null,
        retailer: null,
        department: null,
      },
      isLoading: false
    }
  },
  // Quick-link deep links (routeSeed.js) must seed the filter in created(),
  // BEFORE the filter child mounts: Vue 2 mounts the child before the parent,
  // so seeding in the parent's mounted() lands after the child has snapshotted
  // initialFilter — leaving the Applied-Filters chips blank and dropping the
  // scope on the next Apply. created() runs first, so the child snapshots the
  // seeded filter. Matches BoxTagging's timing.
  created() {
    this.photoFilter = {...this.photoFilter, ...routeFilterSeed(this.$route)}
  },
  mounted() {
    this.getCountItems()
    this.setQCPhotos([])
    this.getPhotos()
  },
  computed: {
    ...mapState({
      photos: state => state.photos.qcPhotos
    })
  },
  methods: {
    ...mapActions(['submitQCAssigned', 'getCountItems']),
    ...mapMutations(['setMessage', 'setQCPhotos']),

    getPhotos() {
      this.setQCPhotos([])
      this.isLoading = true
      // IT-707 P2.1: key names and coercion come from the shared capability
      // builder; this view keeps its own transport and response handling.
      let payload = buildPayload(SURFACES.PHOTO_QC, this.photoFilter)

      return axios.post('/api/photos/quality_control/', payload)
        .then(response => {
          this.isLoading = false
          this.setQCPhotos(response.data)
        })
        .catch(error => {
          this.isLoading = false
          console.log(error)
        })
    },
    setPhotoFilters(newFilter) {
      this.photoFilter = newFilter
      this.getPhotos()
    },
    onCountSaved(photo) {
      const text = photo.is_removed
        ? `Display removed for photo ${photo.response_id}`
        : `Counts updated for photo ${photo.response_id}`
      this.setMessage({ text, type: 'success', alert: true })
    },
    collectPanels() {
      return this.photos
        .map(p => this.$refs[`countPanel_${p.id}`])
        .map(ref => Array.isArray(ref) ? ref[0] : ref)
        .filter(Boolean)
    },
    async saveAllDirty() {
      const panels = this.collectPanels()
      const results = await Promise.allSettled(panels.map(panel => panel.saveIfDirty()))
      return results.filter(r => r.status === 'rejected').map(r => r.reason)
    },
    async submitBatch(payload) {
      const failures = await this.saveAllDirty()
      if (failures.length > 0) {
        const ids = failures.map(f => f.responseId || f.photoId).join(', ')
        this.setMessage({
          text: `Could not save ${failures.length} photo(s): ${ids}. Fix or cancel changes, then resubmit.`,
          type: 'error',
          alert: true
        })
        return
      }
      this.submitQCAssigned().then(() => {
        this.setMessage({ text: 'Photos Marked Reviewed', type: 'success', alert: true })
        this.setQCPhotos([])
        if (payload.getNext) {
          this.getPhotos()
        }
      })
    }
  }
}
</script>

<style scoped>
.border--primary {
  border: 1px solid #666;
  border-radius: 4px;
  padding: 10px;
}
.fixed {
  position: fixed !important;
  bottom: 0 !important;
  z-index: 999;
  background: white;
}
</style>

<style>
@media (min-width: 960px) {
  .count-image {
    max-width: 400px;
  }
}
@media (min-width: 1264px) {
  .count-image {
    max-width: 530px;
  }
}
@media (min-width: 1904px) {
  .count-image {
    max-width: 540px;
  }
}
</style>
